import React from "react";
import { Link } from "react-router-dom";

/**
 * EmptyState — motivational, human-language empty surfaces.
 *
 * Used in place of "No data" / "0 members" across every list view.
 * Keeps the gym operator engaged when a table or board is empty.
 */
export function EmptyState({
  icon: Icon,
  title,
  hint,
  action,
  testid = "empty-state",
  tone = "ops",
}) {
  const TONE_BG = {
    growth: "bg-growth/10 text-growth",
    leads: "bg-leads/10 text-leads",
    trials: "bg-trials/10 text-trials",
    tasks: "bg-tasks/10 text-tasks",
    ai: "bg-ai/10 text-ai",
    risk: "bg-risk/10 text-risk",
    ops: "bg-ops/10 text-ops",
  };
  return (
    <div
      data-testid={testid}
      className="flex flex-col items-center justify-center text-center py-16 px-6"
    >
      {Icon && (
        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 ${TONE_BG[tone] || TONE_BG.ops}`}>
          <Icon className="w-6 h-6" strokeWidth={2.2} />
        </div>
      )}
      <p className="font-display text-base md:text-lg font-semibold text-foreground tracking-tight">
        {title}
      </p>
      {hint && (
        <p className="text-sm text-muted-foreground mt-1.5 max-w-md leading-relaxed">
          {hint}
        </p>
      )}
      {action && <div className="mt-6">{renderAction(action)}</div>}
    </div>
  );
}

function renderAction(action) {
  if (React.isValidElement(action)) return action;
  if (action?.to) {
    return (
      <Link
        to={action.to}
        data-testid={action.testid || "empty-state-action"}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
      >
        {action.label}
      </Link>
    );
  }
  if (action?.onClick) {
    return (
      <button
        type="button"
        onClick={action.onClick}
        data-testid={action.testid || "empty-state-action"}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
      >
        {action.label}
      </button>
    );
  }
  return null;
}

/**
 * Inline empty row — for when a card section is empty but we don't want the
 * full-bleed treatment (e.g., sub-sections inside Dashboard).
 */
export function EmptyRow({ text, testid = "empty-row" }) {
  return (
    <p data-testid={testid} className="text-sm text-muted-foreground/80 py-6 text-center">
      {text}
    </p>
  );
}

export default EmptyState;
